import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material/";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { enums } from "../../../../../common/lib";
import { strings } from "../../../../../common/i18n";
import AuditImages from "../../AuditImages";
import styles from "../styles";
import ControllerUploads from "../../ControllerUploads";
import AssetReject from "../Reject";
import useAssetFoundElseWhere from "./useAssetFoundElseWhere";
import { useAuditStatus } from "../../../../../common/hooks";

import useAssetData from "../useAssetData";
import ExclusionComponent from "./ExclusionComponent";
import NonMandatoryReason from "./NonMandatoryReason";
/**
 * Right Layout Component
 */

export default ({
    assetAuditor,
    displayRejection,
    onRejectHide,
    onRejectSubmit,
    readonly,
    data,
    uploadData,
    validatedAsset,
    onValueChange,

    exclusionReason,
    setExclusionReason,
    buttonLoading,
    setButtonLoading,
    exclusionNote,
    setExclusionNote,
    setNote,
    setNoteType,
    onExclusionSubmit,
    exclusionDatePicker,
    handleDateChange,
    selectedDate
}) => {
    const foundElsewhere = useAssetFoundElseWhere();
    /* Once the audit has been submitted an unrecorded asset is not "not yet" —
     * the auditors are finished and it was never counted. */
    const auditStatus = useAuditStatus();
    const { pendingReview, inReview, closed } = enums.status.audit;
    const auditSubmitted = [pendingReview, inReview, closed].includes(
        auditStatus?.id
    );
    const { isSharedAsset, currentAssetAuditor } = useAssetData(onValueChange);

    if (displayRejection) {
        return (
            <AssetReject
                buttonLoading={buttonLoading}
                setButtonLoading={setButtonLoading}
                onClose={onRejectHide}
                onSubmit={onRejectSubmit}
                readonly={readonly}
                value={data}
            />
        );
    }

    if (isSharedAsset && validatedAsset && currentAssetAuditor.length > 0) {
        return (
            <AuditImages
                uploadData={uploadData}
                assetAuditorId={validatedAsset.id}
                assetAuditorImageId={(validatedAsset || {}).id}
                identifiers={{
                    1: data.asset.identifier1,
                    2: data.asset.identifier2,
                    3: data.asset.identifier3,
                    4: data.asset.identifier4
                }}
                value={(currentAssetAuditor || {}).auditImages}
            />
        );
    }

    if (isSharedAsset && validatedAsset && currentAssetAuditor.length > 0) {
        return (
            <AuditImages
                uploadData={uploadData}
                assetAuditorId={validatedAsset.id}
                assetAuditorImageId={(validatedAsset || {}).id}
                identifiers={{
                    1: data.asset.identifier1,
                    2: data.asset.identifier2,
                    3: data.asset.identifier3,
                    4: data.asset.identifier4
                }}
                value={(currentAssetAuditor || {}).auditImages}
            />
        );
    }

    if (foundElsewhere) {
        return (
            <AuditImages
                uploadData={uploadData}
                assetAuditorId={data.id}
                assetAuditorImageId={(assetAuditor || {}).id}
                identifiers={{
                    1: data.asset.identifier1,
                    2: data.asset.identifier2,
                    3: data.asset.identifier3,
                    4: data.asset.identifier4
                }}
                foundElsewhere={foundElsewhere}
                value={foundElsewhere.images}
            />
        );
    }

    if (!data?.isAssigned) {
        return (
            <Box sx={styles.auditorContainerWapper}>
                {data && data.isMandatory === false ? (
                    <Box sx={styles.centeredBannerWrapper}>
                        <Box sx={styles.nonMandatoryAssetPlaceHolder}>
                            <Typography align="center" color="inherit" variant="h6">
                                {strings("messages.nonMandatoryAsset")}
                            </Typography>
                            <NonMandatoryReason
                                align="center"
                                reason={data.nonMandatoryReason}
                            />
                        </Box>
                    </Box>
                ) : ( 
                    <> 
                    <Box sx={styles.noImagesPlaceHolder} mt={5} mb={5.5}>
                        {data?.reissuedStatus === 1 ? (
                            <Typography align="center" color="inherit" variant="h6">
                                {strings("messages.reissuedAsset")}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    align="center"
                                    color="inherit"
                                    variant="h6"
                                >
                                    {strings(
                                        auditSubmitted
                                            ? "messages.assetNotCountedFinalTitle"
                                            : "messages.assetNotCountedTitle"
                                    )}
                                </Typography>
                                <Typography
                                    align="center"
                                    color="inherit"
                                    variant="body2"
                                >
                                    {strings(
                                        auditSubmitted
                                            ? "messages.assetNotCountedFinalBody"
                                            : "messages.assetNotCountedBody"
                                    )}
                                </Typography>
                            </>
                        )}
                    </Box> 
    
                <ExclusionComponent
                    exclusionNote={exclusionNote}
                    setExclusionNote={setExclusionNote}
                    exclusionReason={exclusionReason}
                    setExclusionReason={setExclusionReason}
                    onExclusionSubmit={onExclusionSubmit}
                    setNoteType={setNoteType}
                    exclusionDatePicker={exclusionDatePicker}
                    handleDateChange={handleDateChange}
                    selectedDate={selectedDate}
                    type="right"
                />
                </>
            )}
        </Box>
    );
    }

    /* An asset is marked assigned as soon as the auditor's scan reaches the
     * sync endpoint, but its notes and images only arrive later, with the batch
     * upload that marks it submitted. Until that lands there may be no auditor
     * record for the asset at all, so this is handled ahead of the assetAuditor
     * branches below — otherwise the panel falls through to the empty return at
     * the bottom and the controller is shown a blank right-hand side. */
    const { excluded } = enums.status.assetStatus;
    const imagesStillPending = data?.isAssigned && !data?.isSubmitted;
    const hasImages = !!assetAuditor?.auditImages?.length;

    if (data?.isAssigned && !hasImages) {
        /* Exclusion is a result the auditor recorded, so it is stated whether or
         * not their submission has arrived — and with the reason when we hold
         * it, since "excluded" on its own tells the controller nothing. */
        if (data.status === excluded) {
            const reason = assetAuditor?.exclusionReason?.name;
            return (
                <Box sx={styles.auditorContainerWapper}>
                    <Box sx={styles.auditorContainer}>
                        <Box sx={styles.noImagesPlaceHolder}>
                            <Typography
                                align="center"
                                color="inherit"
                                variant="h6"
                            >
                                {strings("messages.assetExcludedTitle")}
                            </Typography>
                            <Typography
                                align="center"
                                color="inherit"
                                variant="body2"
                            >
                                {reason
                                    ? strings("messages.assetExcludedWithReason", {
                                          reason
                                      })
                                    : strings("messages.assetExcludedNoReason")}
                            </Typography>
                        </Box>
                        <ControllerUploads uploadData={uploadData} />
                    </Box>
                </Box>
            );
        }

        // Recorded on the auditor's device, but the audit has not been submitted
        // yet, so the images behind that record have not reached us.
        if (imagesStillPending) {
            return (
                <Box sx={styles.auditorContainerWapper}>
                    <Box sx={styles.auditorContainer}>
                        <Box sx={styles.imagesPendingPlaceHolder}>
                            <WarningAmberOutlinedIcon fontSize="small" />
                            <Typography
                                align="center"
                                color="inherit"
                                variant="subtitle2"
                            >
                                {strings("messages.imageNotReceivedTitle")}
                            </Typography>
                            <Typography
                                align="center"
                                color="inherit"
                                variant="body2"
                            >
                                {strings("messages.imageNotReceivedDefault")}
                            </Typography>
                        </Box>
                        <ControllerUploads uploadData={uploadData} />
                    </Box>
                </Box>
            );
        }

        /* This asset's upload has landed and carried no photo, so nothing further
         * is coming for it. A finding about the audit, not a wait. */
        return (
            <Box sx={styles.auditorContainerWapper}>
                <Box sx={styles.auditorContainer}>
                    <Box sx={styles.noImagesPlaceHolder}>
                        <Typography align="center" color="inherit" variant="h6">
                            {strings("messages.noImageReceivedTitle")}
                        </Typography>
                        <Typography align="center" color="inherit" variant="body2">
                            {strings("messages.noImageReceivedBody")}
                        </Typography>
                    </Box>
                    <ControllerUploads uploadData={uploadData} />
                </Box>
            </Box>
        );
    }

    if (assetAuditor && assetAuditor.auditImages.length > 0) {
        return (
            <>
                <AuditImages
                    uploadData={uploadData}
                    assetAuditorId={data.id}
                    assetAuditorImageId={(assetAuditor || {}).id}
                    identifiers={{
                        1: data.asset.identifier1,
                        2: data.asset.identifier2,
                        3: data.asset.identifier3,
                        4: data.asset.identifier4
                    }}
                    value={(assetAuditor || {}).auditImages}
                />

                {data.isMandatory === false && (
                    <Box sx={styles.nonMandatoryAssetPageBanner}>
                        <Typography variant="subtitle2">
                            {strings("messages.nonMandatoryAsset")}
                        </Typography>
                        <NonMandatoryReason reason={data.nonMandatoryReason} />
                    </Box>
                )}
            </>
        );
    }
    return <></>;
};
